<template>
	<div class="cards_container is-flex">
		<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4">
			<template #extra>
				<div v-if="card.efficiency" class="efficiency_extra is-flex is-align-items-center">
					<ChartLine class="efficiency_icon mr-1" />
					<span class="efficiency_label">Eficiencia</span>
					<span class="efficiency_badge ml-auto">{{ card.efficiency }}</span>
				</div>
			</template>
		</CardVentas>
	</div>
</template>

<script>
import { mapState } from "vuex";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import { EXPENSE_CARD_PROGRESS_COLORS } from "@/config/reportStructure";
import {
	formatPercent,
	formatRangeLabel,
	indicesForRange,
	monthsBetween,
} from "@/views/financialReports/utils/periodFormatting";
import { emptyCard } from "@/views/financialReports/utils/summaryCards";

// efficiency: "" → el v-if del slot oculta el badge mientras no haya datos.
// Mismo patrón que grossMargin en CostosTab.
const EMPTY_CARD = () => emptyCard({ efficiency: "" });

export default {
	name: "ExpensesSummaryCards",

	components: { CardVentas, ChartLine },

	props: {
		monthHeaders: {
			type: Array,
			required: true,
		},
		tableRows: {
			type: Array,
			required: true,
		},
		totalRow: {
			type: Object,
			required: true,
		},
		isCuentasMode: {
			type: Boolean,
			required: true,
		},
	},

	computed: {
		...mapState("expensesReport", ["summaryCards"]),

		displayCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},
	},

	methods: {
		buildCard(card, { showMom = false } = {}) {
			const indices = indicesForRange(this.monthHeaders, card.period_start, card.period_end);
			// Si el filtro de la tabla no cubre todo el período de la card,
			// el breakdown desde tableRows quedaría incompleto: lo omitimos.
			const fullyInRange = indices.length === monthsBetween(card.period_start, card.period_end);
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				efficiency: card.efficiency != null ? formatPercent(card.efficiency) : "",
				progressItems: fullyInRange
					? this.topItemsForRange(indices, card.amount)
					: [],
			};
		},

		topItemsForRange(indices, totalAmount) {
			if (indices.length === 0 || totalAmount === 0) return [];

			const baseLevel = this.isCuentasMode ? 2 : 3;
			const baseRows = this.tableRows.filter((row) => row.level === baseLevel);

			// Agrupar por etiqueta — una contraparte puede repetirse en varias categorías
			const aggregated = {};
			baseRows.forEach((row) => {
				if (!aggregated[row.label]) {
					aggregated[row.label] = new Array(row.values.length).fill(0);
				}
				row.values.forEach((val, i) => {
					aggregated[row.label][i] += (val || 0);
				});
			});

			let result = Object.keys(aggregated).map((label, i) => {
				const values = aggregated[label];
				const rangeAmount = indices.reduce((sum, idx) => sum + (values[idx] || 0), 0);
				return {
					label,
					rangeAmount,
					percent: Math.round((rangeAmount / totalAmount) * 100),
					color: EXPENSE_CARD_PROGRESS_COLORS[i % EXPENSE_CARD_PROGRESS_COLORS.length],
				};
			});

			if (!this.isCuentasMode) {
				result.sort((a, b) => b.rangeAmount - a.rangeAmount);
			}

			return result.slice(0, 3).map(item => ({
				label: item.label,
				percent: item.percent,
				color: item.color,
			}));
		},
	},
};
</script>

<style scoped lang="scss">
.cards_container {
	width: 100%;
}

.efficiency_extra {
	width: 100%;
	font-size: 13px;
}

.efficiency_icon {
	width: 14px;
	height: 14px;
	color: var(--report-text-muted-soft);
}

.efficiency_label {
	color: var(--report-text-muted);
	font-weight: 500;
}

.efficiency_badge {
	display: inline-flex;
	align-items: center;
	// El badge consume los tokens del theme gastos (pill-bg/pill-text). Fallback
	// a los hex amber para que mantenga su look fuera de un report_theme_container.
	background: var(--report-pill-bg, #fef3c7);
	color: var(--report-pill-text, #b45309);
	padding: 4px 10px;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 700;
}
</style>
